import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';

@Component({
  selector: 'app-custom-time-picker',
  templateUrl: './custom-time-picker.component.html',
  styleUrls: ['./custom-time-picker.component.scss'],
})
export class CustomTimePickerComponent implements OnInit {
  @Input() isOpen: boolean = false;
  @Input() selectedTime: string = '';
  @Output() timeSelected: EventEmitter<string> = new EventEmitter<string>();
  @Output() closed: EventEmitter<void> = new EventEmitter<void>();

  selectedHour: number = 12;
  selectedMinute: number = 0;
  isAM: boolean = true;
  showingHours: boolean = true; // Para alternar entre horas e minutos

  hours: number[] = Array.from({ length: 12 }, (_, i) => i + 1);
  minutes: number[] = Array.from({ length: 12 }, (_, i) => i * 5); // 0, 5, 10, 15, etc.

  // Posição do ponteiro para horas e minutos
  hourAngle: number = 0;
  minuteAngle: number = 0;

  // Coordenadas do centro do relógio
  centerX: number = 140;
  centerY: number = 140;
  radius: number = 110; // Aumentado para mais espaço

  ngOnInit() {
    this.updateAngles();
  }

  updateAngles() {
    // Calcular ângulo das horas (30 graus por hora)
    this.hourAngle = (this.selectedHour % 12) * 30 - 90;

    // Calcular ângulo dos minutos (6 graus por minuto)
    this.minuteAngle = this.selectedMinute * 6 - 90;
  }

  getHourPosition(hour: number) {
    const angle = (hour * 30 - 90) * (Math.PI / 180);
    const x = this.centerX + this.radius * 0.98 * Math.cos(angle); // Mais próximo da borda
    const y = this.centerY + this.radius * 0.98 * Math.sin(angle);
    return { x: x - 14, y: y - 14 };
  }

  getMinutePosition(minute: number) {
    const angle = (minute * 6 - 90) * (Math.PI / 180);
    const x = this.centerX + this.radius * 0.6 * Math.cos(angle); // Mais interno para os minutos
    const y = this.centerY + this.radius * 0.6 * Math.sin(angle);
    return { x: x - 14, y: y - 14 };
  }

  getHandPosition(isHour: boolean) {
    const angle = isHour ? this.hourAngle : this.minuteAngle;
    const length = isHour ? this.radius * 0.5 : this.radius * 0.7;
    const radian = (angle + 90) * (Math.PI / 180);

    const x = this.centerX + length * Math.cos(radian);
    const y = this.centerY + length * Math.sin(radian);

    return { x, y };
  }

  // Métodos separados para ponteiros - comportamento independente
  getHourHandPosition() {
    const angle = this.hourAngle;
    const length = this.radius * 0.5; // Ponteiro pequeno
    const radian = angle * (Math.PI / 180);

    const x = this.centerX + length * Math.cos(radian);
    const y = this.centerY + length * Math.sin(radian);

    return { x, y };
  }

  getMinuteHandPosition() {
    const angle = this.minuteAngle;
    const length = this.radius * 0.7; // Ponteiro grande
    const radian = angle * (Math.PI / 180);

    const x = this.centerX + length * Math.cos(radian);
    const y = this.centerY + length * Math.sin(radian);

    return { x, y };
  }

  selectHour(hour: number) {
    this.selectedHour = hour;
    this.updateAngles();
    this.showingHours = false; // Mostra os minutos quando selecionar uma hora
  }

  selectMinute(minute: number) {
    this.selectedMinute = minute;
    this.updateAngles();
  }

  togglePeriod() {
    this.isAM = !this.isAM;
  }

  toggleView() {
    this.showingHours = !this.showingHours;
  }

  onCancel() {
    this.isOpen = false;
    this.closed.emit();

    // Resetar para o estado inicial
    this.resetToInitialState();
  }

  convertTo24HourFormat(hour: number, isAM: boolean): string {
    let hour24 = hour;

    if (isAM && hour === 12) {
      hour24 = 0; // 12 AM = 00
    } else if (!isAM && hour !== 12) {
      hour24 = hour + 12; // PM hours except 12 PM
    }
    // 12 PM = 12 (sem mudança)

    return hour24.toString().padStart(2, '0');
  }

  onApply() {
    const hour24 = this.convertTo24HourFormat(this.selectedHour, this.isAM);
    const minute = this.selectedMinute.toString().padStart(2, '0');
    const timeString = `${hour24}:${minute}`;

    this.timeSelected.emit(timeString);
    this.isOpen = false;
    this.closed.emit();

    // Resetar para o estado inicial
    this.resetToInitialState();
  }

  resetToInitialState() {
    this.selectedHour = 12;
    this.selectedMinute = 0;
    this.isAM = true;
    this.showingHours = true;
    this.updateAngles();
  }

  getFormattedTime(): string {
    const hour = this.selectedHour.toString().padStart(2, '0');
    const minute = this.selectedMinute.toString().padStart(2, '0');
    return `${hour}:${minute}`;
  }

  getPeriod(): string {
    return this.isAM ? 'AM' : 'PM';
  }
}
